import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

const STATUS = { pending: ["En attente", "secondary"], approved: ["Validé", "default"], rejected: ["Refusé", "destructive"], suspended: ["Suspendu", "outline"] };

const Row = ({ p, canEdit, onChange }) => {
  const { money, date } = useFormat();
  const set = async (status) => { try { await api.put(`/admin/partners/${p.id}`, { status }); toast.success("Statut mis à jour"); onChange(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <li className="flex flex-wrap items-center gap-3 p-4 text-sm" data-testid={`admin-partner-${p.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2"><b>{p.company}</b><Badge variant={STATUS[p.status][1]} className="rounded-full" data-testid={`admin-partner-status-${p.id}`}>{STATUS[p.status][0]}</Badge></div>
        <div className="text-xs text-muted-foreground">{p.contact_name} · {p.email} · {p.phone}{p.siret && ` · SIRET ${p.siret}`} · inscrit le {date(p.created_at)}</div>
        <div className="text-xs">{p.sector_labels.join(", ")}</div>
      </div>
      <div className="text-right text-xs"><div className="num font-bold">{money(p.revenue)}</div><div className="text-muted-foreground">{p.leads_bought} lead(s)</div></div>
      {canEdit && <div className="flex gap-1.5">
        {p.status !== "approved" && <Button size="sm" className="rounded-full" onClick={() => set("approved")} data-testid={`admin-partner-approve-${p.id}`}>Valider</Button>}
        {p.status === "pending" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => set("rejected")} data-testid={`admin-partner-reject-${p.id}`}>Refuser</Button>}
        {p.status === "approved" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => set("suspended")} data-testid={`admin-partner-suspend-${p.id}`}>Suspendre</Button>}
      </div>}
    </li>
  );
};

export const PartnersAdmin = () => {
  const { user } = useAuth();
  const { money } = useFormat();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-partners"], queryFn: () => api.get("/admin/partners").then((r) => r.data) });
  if (!data) return <Skeleton className="h-48 rounded-2xl" />;
  const cells = [["À valider", data.stats.pending], ["Leads créés", data.stats.leads], ["Chiffre d'affaires leads", money(data.stats.revenue)]];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-3">{cells.map(([l, v]) => <div key={l} className="card-surface p-4" data-testid={`admin-partners-stat-${l}`}><div className="num text-xl font-bold">{v}</div><div className="text-xs text-muted-foreground">{l}</div></div>)}</div>
      {data.partners.length ? <ul className="card-surface divide-y divide-border" data-testid="admin-partners-list">{data.partners.map((p) => <Row key={p.id} p={p} canEdit={user.platform_role === "admin"} onChange={() => qc.invalidateQueries({ queryKey: ["admin-partners"] })} />)}</ul>
        : <p className="text-sm text-muted-foreground">Aucun partenaire inscrit.</p>}
    </div>
  );
};
